import React, { useMemo } from "react";

import { useDispatch, useSelector } from "services/redux/rootReducer";

import GenericEditorPage from "features/shared/screens/genericEditorPage";
import articleForm from "../components/form/articleForm";
import useImportExport from "features/shared/hooks/useImportExport";

import { Article, ArticleType } from "../types/article";

import addOneArticle from "../actions/update/set.addOneArticle";
import updateOneArticle from "../actions/update/set.updateOneArticle";
import removeManyArticles from "../actions/update/set.removeManyArticles";

import ARTICLE_TABLE from "../constants/table";
import MODAL_CONFIG from "../constants/modal";
import { initialFields } from "../constants/form";

import { flattenArticle, unflattenArticle } from "../functions/excel";
import { searchArrayOfArticles } from "../functions/search";
import { verifyForm } from "../functions/form";
import addManyArticles from "../actions/update/set.addManyArticles";

interface Props {
    articleType: ArticleType;
}

const ArticleEditorPage: React.FC<Props> = ({ articleType }) => {
    const dispatch = useDispatch();
    const articles = useSelector((state) => state.articles);
    const tags = useSelector((state) => state.tags);
    const { sortedData } = useSelector((state) => state.table);

    const filteredArticles = useMemo(() => {
        return articles.filter((article) => article.type === articleType);
    }, [articles, articleType]);

    const [handleImportData, handleExportData] = useImportExport({
        data: sortedData as Article[],
        collectionName: "articles",
        exportFileName:
            articleType === ArticleType.News
                ? "news"
                : articleType.toLowerCase() + "s",
        unflattenMapperFunction: unflattenArticle,
        flattenMapperFunction: flattenArticle,
        verificationFunction: async (article) => {
            return await verifyForm({
                fields: article,
                tags,
            });
        },
        onImportSubmitCallback: async (importedData) => {
            dispatch(addManyArticles(importedData));
        },
    });

    const handleAddArticle = (fields: Article) => {
        dispatch(addOneArticle(fields));
    };

    const handleUpdateArticle = (id: string, article: Article) => {
        dispatch(updateOneArticle(id, article));
    };

    const handleRemoveArticles = (ids: string[]) => {
        dispatch(removeManyArticles(ids));
    };

    return (
        <GenericEditorPage
            data={filteredArticles}
            indexer="id"
            initialForm={{
                ...initialFields,
                type: articleType,
            }}
            tableDefinition={ARTICLE_TABLE}
            modalConfig={MODAL_CONFIG}
            form={articleForm}
            onAddCallback={handleAddArticle}
            onRawDataSubmitCallback={handleImportData}
            onUpdateCallback={handleUpdateArticle}
            onRemoveCallback={handleRemoveArticles}
            onExportCallback={handleExportData}
            searchFunction={searchArrayOfArticles}
        />
    );
};

export default ArticleEditorPage;
